"use client";

import { useQueryClient } from "@tanstack/react-query";
import type {
  MindstringPrototype,
  ResetSituationsConfigsRequest,
  Situation,
  SupportedLanguage,
  UpdateSituationConfigRequest,
} from "@/generated/fastapi";
import GenericDrawerInfo from "@/shared/ui/display/GenericDrawerInfo";
import GenericEmptyComponent from "@/shared/ui/GenericEmptyComponent";
import GenericInteractiveGuidance from "@/shared/ui/display/GenericInteractiveGuidance";
import GenericSelector from "@/shared/ui/controls/GenericSelector";
import LoadingState from "@/shared/ui/LoadingState";
import { useState, useEffect, useMemo } from "react";
import { useOptionItems } from "@/shared/ui/controls/useOptionItems";
import {
  Alert,
  Box,
  Button,
  HStack,
  Heading,
  Text,
  VStack,
} from "@chakra-ui/react";
import GenericToggle from "@/shared/ui/controls/GenericToggle";
import GenericEditable from "@/shared/ui/controls/GenericEditable";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import { useUserProfile } from "@/state/globalStore";
import { showErrorToast, showSuccessToast } from "@/shared/feedback/toast";
import { mindstringPrototypes } from "@/shared/config/backend";
import { getPrototypeBadgeTokens } from "@/shared/config/prototypeTheme";
import WithLongPress from "@/shared/ui/gestures/WithLongPress";
import {
  useSituationAccessors,
  situationsKeys,
  useSituationsQuery,
  useResetSituationsConfigsMutation,
  useUpdateSituationConfigMutation,
} from "@/features/situations";
import {
  buildSituationConfigPatch,
  cloneSituationConfig,
  getResolvedSituationOutputConfig,
  setMaskContextEnabled,
  setMaskContextText,
  setMaskPositionText,
  setOverrideContextEnabled,
  setOverrideContextText,
  setOverrideLocationEnabled,
  setOverrideLocationText,
  setOverridePositionEnabled,
  setOverridePositionText,
  setPositionSnippetEnabled,
  setPositionSnippetText,
  setShowContext,
  setShowDateFrom,
  setShowDateTo,
  setShowLocation,
  setShowPosition,
  type SituationConfig,
  type SituationConfigTouchedField,
} from "../config/situationConfigUpdates";

// ---------------------------------
// Hook: useSituationConfig
// ---------------------------------

const TOAST_SUCCESS_TIMEOUT = 800;

function useSituationConfig() {
  const queryClient = useQueryClient();
  const updateSituationConfigMutation = useUpdateSituationConfigMutation();
  const resetSituationsConfigsMutation = useResetSituationsConfigsMutation();

  const getSituationSnapshots = () =>
    queryClient.getQueriesData<Situation[]>({
      queryKey: situationsKeys.all,
    });

  const restoreSituationSnapshots = (
    snapshots: readonly [readonly unknown[], Situation[] | undefined][],
  ) => {
    snapshots.forEach(([queryKey, data]) => {
      queryClient.setQueryData(queryKey, data);
    });
  };

  const setSituationConfigsInCache = (
    updatedConfigs: Record<string, SituationConfig>,
  ) => {
    queryClient.setQueriesData<Situation[] | undefined>(
      { queryKey: situationsKeys.all },
      (currentSituations) =>
        currentSituations?.map((situation) =>
          updatedConfigs[situation.id]
            ? { ...situation, config: updatedConfigs[situation.id] }
            : situation,
        ),
    );
  };

  async function saveSingleSituationConfig(
    situationId: string,
    config: SituationConfig,
  ) {
    const previousSnapshots = getSituationSnapshots();
    setSituationConfigsInCache({ [situationId]: config });

    try {
      const request: UpdateSituationConfigRequest = {
        situation_ids: [situationId],
        config,
      };
      const result = await updateSituationConfigMutation.mutateAsync(request);
      const updatedConfig = result.updated_configs?.[situationId];

      if (result.status === "success" && updatedConfig) {
        setSituationConfigsInCache({ [situationId]: updatedConfig });
        showSuccessToast("Profile export settings saved", TOAST_SUCCESS_TIMEOUT);
        return updatedConfig;
      } else {
        restoreSituationSnapshots(previousSnapshots);
        showErrorToast(result.message || "Failed to save profile export settings");
        return null;
      }
    } catch {
      restoreSituationSnapshots(previousSnapshots);
      showErrorToast("Failed to save profile export settings");
      return null;
    }
  }

  async function saveBatchSituationConfigPatch({
    config,
    language,
    prototype,
    situationIds,
    touchedFields,
  }: {
    config: SituationConfig;
    language: SupportedLanguage;
    prototype: MindstringPrototype;
    situationIds: string[];
    touchedFields: Set<SituationConfigTouchedField>;
  }) {
    if (touchedFields.size === 0) {
      showErrorToast("Change at least one setting before saving.");
      return null;
    }

    const previousSnapshots = getSituationSnapshots();
    const request: UpdateSituationConfigRequest = {
      situation_ids: situationIds,
      patch: buildSituationConfigPatch(config, touchedFields),
      language,
      prototype,
    };

    try {
      const result = await updateSituationConfigMutation.mutateAsync(request);
      const updatedConfigs = result.updated_configs ?? {};

      if (result.status === "success" && Object.keys(updatedConfigs).length > 0) {
        setSituationConfigsInCache(updatedConfigs);
        showSuccessToast("Profile export settings saved", TOAST_SUCCESS_TIMEOUT);
        return result;
      }

      restoreSituationSnapshots(previousSnapshots);
      showErrorToast(result.message || "Failed to save profile export settings");
      return null;
    } catch {
      restoreSituationSnapshots(previousSnapshots);
      showErrorToast("Failed to save profile export settings");
      return null;
    }
  }

  async function resetSituationConfigsSync(situationIds: string[]) {
    const previousSnapshots = getSituationSnapshots();
    try {
      const request: ResetSituationsConfigsRequest = {
        situation_ids: situationIds,
      };

      const initSituationConfig =
        await resetSituationsConfigsMutation.mutateAsync(request);
      if (initSituationConfig?.status === "success") {
        const updatedConfigs = initSituationConfig.updated_configs;
        if (!updatedConfigs) {
          restoreSituationSnapshots(previousSnapshots);
          showErrorToast(
            initSituationConfig.message ||
              "Failed to reset profile export settings",
          );
          return null;
        }

        const resetConfigCount = Object.keys(updatedConfigs).length;

        if (resetConfigCount > 0) {
          setSituationConfigsInCache(updatedConfigs);
          showSuccessToast(
            resetConfigCount === 1
              ? "Profile export settings reset"
              : "Profile export settings reset for selected situations",
            TOAST_SUCCESS_TIMEOUT,
          );
          return initSituationConfig;
        } else {
          restoreSituationSnapshots(previousSnapshots);
          showErrorToast(
            initSituationConfig.message ||
              "Failed to reset profile export settings",
          );
          return null;
        }
      } else {
        restoreSituationSnapshots(previousSnapshots);
        showErrorToast(
          initSituationConfig?.message ||
            "Failed to reset profile export settings",
        );
        return null;
      }
    } catch {
      restoreSituationSnapshots(previousSnapshots);
      showErrorToast("Failed to reset profile export settings");
      return null;
    }
  }

  return {
    resetSituationConfigsSync,
    saveBatchSituationConfigPatch,
    saveSingleSituationConfig,
  };
}

// ---------------------------------
// Component: SituationConfig
// ---------------------------------

interface SituationConfigProps {
  mode?: "single" | "batch";
  onCancel?: () => void;
  prototype: MindstringPrototype;
  situationId?: string;
  situationIds?: string[];
}

export default function SituationConfig({
  mode = "single",
  onCancel,
  prototype,
  situationId,
  situationIds,
}: SituationConfigProps) {
  const { languageItems } = useOptionItems();
  const situationsQuery = useSituationsQuery();
  const { situations } = useSituationAccessors(situationsQuery.data);
  const {
    resetSituationConfigsSync,
    saveBatchSituationConfigPatch,
    saveSingleSituationConfig,
  } = useSituationConfig();
  const { userProfile } = useUserProfile();
  const [activeLanguages, setActiveLanguages] = useState<SupportedLanguage[]>([
    userProfile.data.print_settings.print_language,
  ]);
  const activeLanguage = activeLanguages[0];
  const targetSituationIds = useMemo(
    () => (mode === "batch" ? (situationIds ?? []) : situationId ? [situationId] : []),
    [mode, situationId, situationIds],
  );
  const targetSituations = useMemo(
    () =>
      targetSituationIds
        .map((id) => situations.find((situation) => situation.id === id))
        .filter((situation): situation is Situation => Boolean(situation)),
    [situations, targetSituationIds],
  );
  const situation = mode === "single" ? targetSituations[0] : undefined;
  const baseConfig = targetSituations[0]?.config;
  const isBatchMode = mode === "batch";
  const isSingleMode = !isBatchMode;
  const activeLanguageLabel =
    languageItems.find((item) => item.value === activeLanguage)?.label ??
    activeLanguage.toUpperCase();
  const prototypeLabel = mindstringPrototypes[prototype];
  const prototypeBadgeTokens = getPrototypeBadgeTokens(prototype);
  const [resetKey, setResetKey] = useState(0);
  const [isDirty, setIsDirty] = useState(false);
  const [touchedFields, setTouchedFields] = useState<
    Set<SituationConfigTouchedField>
  >(new Set());
  const [situationConfigLocal, setSituationConfigLocal] = useState<
    SituationConfig | null
  >(baseConfig ? cloneSituationConfig(baseConfig) : null);

  useEffect(() => {
    // Query-backed config is the external source for this drawer draft.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSituationConfigLocal(baseConfig ? cloneSituationConfig(baseConfig) : null);
    setIsDirty(false);
    setTouchedFields(new Set());
    setResetKey((prev) => prev + 1);
  }, [baseConfig, mode]);

  if (situationsQuery.isLoading) {
    return (
      <LoadingState
        loadingMessage="Loading profile export settings"
        message="Preparing the selected situation profile export settings."
      />
    );
  }

  if (!situationConfigLocal || targetSituationIds.length === 0) {
    return (
      <GenericEmptyComponent
        icon="settings_print"
        title="Profile export settings unavailable"
        message="Choose at least one situation before adjusting profile export settings."
      />
    );
  }

  const resolvedConfig = getResolvedSituationOutputConfig(
    situationConfigLocal,
    activeLanguage,
    prototype,
  );
  const saveDisabledReason = !isDirty
    ? "Make a change before saving."
    : isBatchMode && touchedFields.size === 0
      ? "Choose at least one setting to apply."
      : null;

  async function commitSingleConfig(nextConfig: SituationConfig) {
    const savedConfig = await saveSingleSituationConfig(
      targetSituationIds[0],
      nextConfig,
    );

    if (savedConfig) {
      setSituationConfigLocal(cloneSituationConfig(savedConfig));
      setIsDirty(false);
      setTouchedFields(new Set());
    }
  }

  function updateDraft(
    nextConfig: SituationConfig,
    touchedField: SituationConfigTouchedField,
  ) {
    setSituationConfigLocal(nextConfig);
    if (isBatchMode) {
      setIsDirty(true);
      setTouchedFields((current) => new Set(current).add(touchedField));
    }
  }

  async function updateAndMaybeSave(
    nextConfig: SituationConfig,
    touchedField: SituationConfigTouchedField,
    saveNow = true,
  ) {
    updateDraft(nextConfig, touchedField);

    if (isSingleMode && saveNow) {
      await commitSingleConfig(nextConfig);
    }
  }

  async function handleSave() {
    if (saveDisabledReason) {
      return;
    }

    const configToSave = situationConfigLocal;
    if (!configToSave) {
      return;
    }

    const response = isBatchMode
      ? await saveBatchSituationConfigPatch({
          config: configToSave,
          language: activeLanguage,
          prototype,
          situationIds: targetSituationIds,
          touchedFields,
        })
      : await saveSingleSituationConfig(targetSituationIds[0], configToSave);

    if (response) {
      setIsDirty(false);
      setTouchedFields(new Set());
    }
  }

  async function handleReset() {
    const response = await resetSituationConfigsSync(targetSituationIds);
    const updatedConfigs = response?.updated_configs;
    const resetConfig = updatedConfigs?.[targetSituationIds[0]];

    if (resetConfig) {
      setSituationConfigLocal(cloneSituationConfig(resetConfig));
      setIsDirty(false);
      setTouchedFields(new Set());
      setResetKey((prev) => prev + 1);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent, submitFunction: () => void) {
    if (e.key === "Enter") {
      e.preventDefault();
      submitFunction();
    }
  }

  return (
    <VStack align="stretch" spaceY={4}>
      <GenericDrawerInfo
        description={
          isBatchMode
            ? "Choose how selected situations appear in future profile exports. Save applies only changed settings to the selected language and perspective."
            : "Choose how this situation appears in future profile exports. Changes save as you apply them."
        }
        items={[
          {
            label: isBatchMode
              ? `${targetSituationIds.length} selected situations`
              : situation?.position || "Situation",
            colorPalette: "blue",
          },
          { label: prototypeLabel, ...prototypeBadgeTokens },
          { label: activeLanguageLabel, colorPalette: "teal" },
        ]}
      />

      <HStack justify="space-between" align="start" gap="3" flexWrap="wrap">
        <HStack
          flex="1"
          minW="0"
          gap="2"
          borderWidth="1"
          borderColor="moveon.surface.border"
          borderRadius="md"
          p="1"
        >
          <Box flex="1" minW="0" overflow="hidden">
            <GenericSelector<SupportedLanguage>
              previewItems={languageItems}
              stateObject={activeLanguages}
              stateObjectSetter={setActiveLanguages}
              disableClear={true}
              suppressDefaultStyles={true}
            />
          </Box>
        </HStack>

        <HStack gap="2">
          {onCancel ? (
            <Button variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
          ) : null}
          {isBatchMode ? (
            <Button
              bg="moveon.primary"
              color="white"
              disabled={Boolean(saveDisabledReason)}
              onClick={handleSave}
            >
              <GenericIcon icon="save" />
              Save settings
            </Button>
          ) : null}
        </HStack>
      </HStack>

      {isBatchMode && saveDisabledReason ? (
        <Text fontSize="xs" color="moveon.text.muted">
          {saveDisabledReason}
        </Text>
      ) : null}

      {isBatchMode ? (
        <GenericInteractiveGuidance
          title="Batch changes need confirmation"
          message="Single-situation settings save immediately. In batch mode, changes stay as a draft until you click Save settings."
          compactMessage="Changes stay as a draft until you click Save settings."
          icon="save"
          accentColor="moveon.warning"
          accentBg="moveon.warningLight"
          accentBorderColor="moveon.warning"
        />
      ) : null}

      <HStack justify="flex-end">
        <WithLongPress
          onLongPress={handleReset}
          enabled={true}
          longPressHint={
            isBatchMode
              ? "Hold to reset all selected situations"
              : "Hold to reset this situation"
          }
          threshold="critical"
        >
          <Button variant="ghost" color="moveon.danger">
            <GenericIcon icon="reset" />
            {isBatchMode ? "Reset selected" : "Reset situation"}
          </Button>
        </WithLongPress>
      </HStack>

      <Heading fontSize="sm" fontWeight="semibold" mb="4">
        Situation header
      </Heading>

      <GenericToggle
        key={`show-position-toggle-${resolvedConfig.show_position}`}
        label="Show role or title"
        description="Use the stored situation title in future profile exports."
        value={resolvedConfig.show_position}
        onChange={(newValue) =>
          updateAndMaybeSave(
            setShowPosition(situationConfigLocal, newValue),
            "show_position",
          )
        }
        minW="48"
      />
      {!resolvedConfig.show_position && (
        <GenericEditable
          key={`position-mask-${activeLanguage}-${resetKey}`}
          label={`Masked title (${activeLanguage.toUpperCase()})`}
          placeholder="... masked position text ..."
          value={resolvedConfig.mask_position || ""}
          onChange={(text) =>
            updateDraft(
              setMaskPositionText(situationConfigLocal, activeLanguage, text),
              "mask_position",
            )
          }
          onSubmit={() => void commitSingleConfig(situationConfigLocal)}
          onClear={() =>
            void updateAndMaybeSave(
              setMaskPositionText(situationConfigLocal, activeLanguage, ""),
              "mask_position",
            )
          }
          handleKeyDown={handleKeyDown}
          disabled={resolvedConfig.show_position}
        />
      )}
      {isSingleMode && resolvedConfig.show_position && (
        <GenericToggle
          key={`override-position-toggle-${resolvedConfig.override_position.enabled}`}
          label="Use custom title"
          description="Replace the stored title in future profile exports."
          value={resolvedConfig.override_position.enabled}
          onChange={(enabled) =>
            updateAndMaybeSave(
              setOverridePositionEnabled(
                situationConfigLocal,
                activeLanguage,
                enabled,
              ),
              "override_position",
            )
          }
          minW="48"
        />
      )}
      {isSingleMode && resolvedConfig.override_position.enabled && (
        <GenericEditable
          key={`position-override-${activeLanguage}-${resetKey}`}
          label={`Custom title (${activeLanguage.toUpperCase()})`}
          placeholder="... custom position ..."
          value={resolvedConfig.override_position.text || ""}
          onChange={(text) =>
            updateDraft(
              setOverridePositionText(situationConfigLocal, activeLanguage, text),
              "override_position",
            )
          }
          onSubmit={() => void commitSingleConfig(situationConfigLocal)}
          onClear={() =>
            void updateAndMaybeSave(
              setOverridePositionText(situationConfigLocal, activeLanguage, ""),
              "override_position",
            )
          }
          handleKeyDown={handleKeyDown}
          disabled={!resolvedConfig.override_position.enabled}
        />
      )}
      {isSingleMode ? (
        <>
          <GenericToggle
            key={`position-snippet-toggle-${activeLanguage}-${prototype}`}
            label="Use short export summary"
            description="Add a short summary underneath the situation title."
            value={resolvedConfig.position_snippet.enabled}
            onChange={(enabled) =>
              updateAndMaybeSave(
                setPositionSnippetEnabled(
                  situationConfigLocal,
                  activeLanguage,
                  prototype,
                  enabled,
                ),
                "position_snippet",
              )
            }
            minW="48"
          />
          {resolvedConfig.position_snippet.enabled && (
            <>
              <GenericEditable
                key={`position-snippet-${activeLanguage}-${resetKey}`}
                isTextarea={true}
                label={`Short output summary (${activeLanguage.toUpperCase()})`}
                placeholder={`extended description for position in perspective: ${mindstringPrototypes[prototype]}`}
                value={resolvedConfig.position_snippet.text || ""}
                onChange={(text) =>
                  updateDraft(
                    setPositionSnippetText(
                      situationConfigLocal,
                      activeLanguage,
                      prototype,
                      text,
                    ),
                    "position_snippet",
                  )
                }
                onSubmit={() => void commitSingleConfig(situationConfigLocal)}
                onClear={() =>
                  void updateAndMaybeSave(
                    setPositionSnippetText(
                      situationConfigLocal,
                      activeLanguage,
                      prototype,
                      "",
                    ),
                    "position_snippet",
                  )
                }
                handleKeyDown={handleKeyDown}
                disabled={!resolvedConfig.position_snippet.enabled}
              />
              <Text fontSize="xs" mt="1">
                {resolvedConfig.position_snippet.text?.length || 0} characters
              </Text>
            </>
          )}
        </>
      ) : null}

      <Heading fontSize="sm" fontWeight="semibold" mb="4">
        Privacy
      </Heading>
      <GenericToggle
        key={`show-context-toggle-${resolvedConfig.show_context}`}
        label="Show context"
        description="Use the stored context in future profile exports."
        value={resolvedConfig.show_context}
        onChange={(newValue) =>
          updateAndMaybeSave(
            setShowContext(situationConfigLocal, prototype, newValue),
            "show_context",
          )
        }
        minW="48"
      />
      {!resolvedConfig.show_context && (
        <GenericToggle
          key={`context-mask-toggle-${resolvedConfig.mask_context.enabled}`}
          label="Mask Context"
          description="Use masked context text instead of stored context. If disabled, context print will be skipped entirely."
          value={resolvedConfig.mask_context.enabled}
          onChange={(enabled) =>
            updateAndMaybeSave(
              setMaskContextEnabled(
                situationConfigLocal,
                activeLanguage,
                prototype,
                enabled,
              ),
              "mask_context",
            )
          }
          minW="48"
        />
      )}

      {isSingleMode &&
        !resolvedConfig.show_context &&
        resolvedConfig.mask_context.enabled && (
        <GenericEditable
          key={`context-mask-${activeLanguage}-${resetKey}`}
          label={`Masked Context Text (${activeLanguage.toUpperCase()})`}
          placeholder="... masked context text ..."
          value={resolvedConfig.mask_context.text || ""}
          onChange={(text) =>
            updateDraft(
              setMaskContextText(
                situationConfigLocal,
                activeLanguage,
                prototype,
                text,
              ),
              "mask_context",
            )
          }
          onSubmit={() => void commitSingleConfig(situationConfigLocal)}
          onClear={() =>
            void updateAndMaybeSave(
              setMaskContextText(
                situationConfigLocal,
                activeLanguage,
                prototype,
                "",
              ),
              "mask_context",
            )
          }
          handleKeyDown={handleKeyDown}
          disabled={resolvedConfig.show_context}
        />
      )}

      {isSingleMode ? (
        <>
          <Heading fontSize="sm" fontWeight="semibold" mb="4">
            Custom wording
          </Heading>
          {resolvedConfig.show_context && (
            <GenericToggle
              key={`override-context-toggle-${resolvedConfig.override_context.enabled}`}
              label="Override Context"
              description="Custom wording replacing stored context"
              value={resolvedConfig.override_context.enabled}
              onChange={(enabled) =>
                updateAndMaybeSave(
                  setOverrideContextEnabled(
                    situationConfigLocal,
                    activeLanguage,
                    prototype,
                    enabled,
                  ),
                  "override_context",
                )
              }
              minW="48"
            />
          )}
          {resolvedConfig.override_context.enabled && (
            <GenericEditable
              key={`context-override-${activeLanguage}-${resetKey}`}
              label={`Context Text (${activeLanguage.toUpperCase()})`}
              placeholder="... custom context ..."
              value={resolvedConfig.override_context.text || ""}
              onChange={(text) =>
                updateDraft(
                  setOverrideContextText(
                    situationConfigLocal,
                    activeLanguage,
                    prototype,
                    text,
                  ),
                  "override_context",
                )
              }
              onSubmit={() => void commitSingleConfig(situationConfigLocal)}
              onClear={() =>
                void updateAndMaybeSave(
                  setOverrideContextText(
                    situationConfigLocal,
                    activeLanguage,
                    prototype,
                    "",
                  ),
                  "override_context",
                )
              }
              handleKeyDown={handleKeyDown}
              disabled={!resolvedConfig.override_context.enabled}
            />
          )}
        </>
      ) : null}

      <Heading fontSize="sm" fontWeight="semibold" mb="4">
        Location {situation?.location ? `(${situation.location})` : ""}
      </Heading>
      <GenericToggle
        key={`show-location-toggle-${resolvedConfig.show_location}`}
        label="Show Location"
        description="Print location (as stored)"
        value={resolvedConfig.show_location}
        onChange={(newValue) =>
          updateAndMaybeSave(
            setShowLocation(situationConfigLocal, newValue),
            "show_location",
          )
        }
        minW="48"
      />
      {isSingleMode && resolvedConfig.show_location && (
        <GenericToggle
          key={`override-location-toggle-${resolvedConfig.override_location.enabled}`}
          label="Override Location"
          description="Custom wording replacing stored location"
          value={resolvedConfig.override_location.enabled}
          onChange={(enabled) =>
            updateAndMaybeSave(
              setOverrideLocationEnabled(
                situationConfigLocal,
                activeLanguage,
                enabled,
              ),
              "override_location",
            )
          }
          minW="48"
        />
      )}
      {isSingleMode &&
        resolvedConfig.show_location &&
        resolvedConfig.override_location.enabled && (
          <GenericEditable
            key={`location-override-${activeLanguage}-${resetKey}`}
            label={`Location Text (${activeLanguage.toUpperCase()})`}
            placeholder="... custom location ..."
            value={resolvedConfig.override_location.text || ""}
            onChange={(text) =>
              updateDraft(
                setOverrideLocationText(
                  situationConfigLocal,
                  activeLanguage,
                  text,
                ),
                "override_location",
              )
            }
            onSubmit={() => void commitSingleConfig(situationConfigLocal)}
            onClear={() =>
              void updateAndMaybeSave(
                setOverrideLocationText(situationConfigLocal, activeLanguage, ""),
                "override_location",
              )
            }
            handleKeyDown={handleKeyDown}
            disabled={!resolvedConfig.override_location.enabled}
          />
        )}

      <Heading fontSize="sm" fontWeight="semibold" mb="4">
        Dates{" "}
        {situation
          ? `(${situation.date_from || "N/A"} - ${situation.date_to || "N/A"})`
          : ""}
      </Heading>
      {userProfile.data.print_settings.timeline_framing === "numerical" ? (
        <>
          <GenericToggle
            key={`show-date-from-toggle-${resolvedConfig.show_date_from}`}
            label="Show Start Date"
            description="Print start date information"
            value={resolvedConfig.show_date_from}
            onChange={(newValue) =>
              updateAndMaybeSave(
                setShowDateFrom(situationConfigLocal, prototype, newValue),
                "show_date_from",
              )
            }
            minW="48"
          />
          <GenericToggle
            key={`show-date-to-toggle-${resolvedConfig.show_date_to}`}
            label="Show End Date"
            description="Print end date information"
            value={resolvedConfig.show_date_to}
            onChange={(newValue) =>
              updateAndMaybeSave(
                setShowDateTo(situationConfigLocal, prototype, newValue),
                "show_date_to",
              )
            }
            minW="48"
          />
        </>
      ) : (
        <Alert.Root status="info">
          <Alert.Indicator />
          <Alert.Title>
            Dates are hidden or controlled by the system. See general Print
            Settings.
          </Alert.Title>
        </Alert.Root>
      )}
    </VStack>
  );
}
